import React from 'react';
import { Download, User, Building2, Sparkles } from 'lucide-react';
import { skillLibrary } from '../data/skillLibrary';
import BrandCover from './BrandCover';
import './Library.css';

const PERSONAL_STEPS = [
  'Download the skill\'s .zip file below and unzip it: you\'ll get a folder containing a SKILL.md file (and any supporting scripts).',
  'In claude.ai, go to Settings → Capabilities → Skills.',
  'Click "Upload skill" and select the unzipped folder.',
  'Claude confirms the skill is installed: it\'s now available in every new conversation, with nothing to re-upload.',
];

const ORG_STEPS = [
  'An account Owner or Admin signs in to your organisation\'s Claude Console.',
  'Go to Organization Settings → Capabilities → Skills.',
  'Upload the same unzipped skill folder there.',
  'Once enabled, the skill becomes available to every member of the organisation automatically: nobody needs to install it individually.',
];

function groupByCourse(items) {
  const groups = [];
  const byKey = new Map();
  items.forEach(item => {
    const key = item.courseId || 'general';
    if (!byKey.has(key)) {
      const group = { courseId: key, courseTitle: item.courseTitle || 'General Resources', items: [] };
      byKey.set(key, group);
      groups.push(group);
    }
    byKey.get(key).items.push(item);
  });
  return groups;
}

export default function Library() {
  const featuredSkills = skillLibrary.filter(skill => skill.featured);
  const groups = groupByCourse(skillLibrary.filter(skill => !skill.featured));

  return (
    <div className="library">
      <div className="library-header">
        <div>
          <h2 className="library-title">Resource Library</h2>
          <p className="library-sub">Download Claude Skills from your courses to install on your own Claude account.</p>
        </div>
      </div>

      <div className="library-intro">
        A Claude <strong>Skill</strong> is a packaged set of instructions and, optionally, reference files or scripts that teaches Claude how to complete a specific task exactly the way your organisation wants it done, so you don't have to re-explain the format, tone, or rules every time.
      </div>

      {featuredSkills.length > 0 && (
        <section className="library-featured" aria-label="Featured skills">
          {featuredSkills.map(skill => (
            <div className="library-featured-card" key={skill.slug}>
              <div className="library-featured-cover">
                <BrandCover title={skill.name} category="Claude Skill" resource decorative />
              </div>
              <div className="library-featured-body">
                <div className="library-featured-badge">
                  <Sparkles size={13} strokeWidth={2} aria-hidden="true" /> Featured skill
                </div>
                <h3 className="library-featured-name">{skill.name}</h3>
                <div className="library-featured-desc">{skill.description}</div>
                {skill.featuredReason && (
                  <div className="library-featured-why">
                    <strong>Why it matters:</strong> {skill.featuredReason}
                  </div>
                )}
                <a className="btn btn-primary library-skill-btn" href={skill.zip} download>
                  <Download size={14} strokeWidth={1.75} aria-hidden="true" /> Download .zip<span className="sr-only"> for {skill.name}</span>
                </a>
              </div>
            </div>
          ))}
        </section>
      )}

      {groups.length === 0 ? (
        <div className="library-empty" role="status">No resources are available yet, check back after your next session.</div>
      ) : (
        groups.map(group => (
          <section className="library-group" key={group.courseId}>
            <h3 className="library-group-title">{group.courseTitle}</h3>
            <div className="library-skill-grid">
              {group.items.map(skill => (
                <div className="library-skill-card" key={skill.slug}>
                  <div className="library-skill-cover">
                    <BrandCover title={skill.name} category="Claude Skill" resource decorative />
                  </div>
                  <div className="library-skill-body">
                    <h4 className="library-skill-name">{skill.name}</h4>
                    <div className="library-skill-desc">{skill.description}</div>
                    <a className="btn btn-outline library-skill-btn" href={skill.zip} download>
                      <Download size={14} strokeWidth={1.75} aria-hidden="true" /> Download .zip<span className="sr-only"> for {skill.name}</span>
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </section>
        ))
      )}

      <section className="library-guide">
        <h3 className="library-group-title">Installing a Skill</h3>
        <p className="library-guide-note">A video walkthrough of these steps is coming soon. For now, follow the instructions below.</p>
        <div className="library-guide-cols">
          <div className="library-guide-col">
            <h4 className="library-guide-col-head">
              <User size={16} strokeWidth={1.75} aria-hidden="true" />
              <span>For yourself</span>
            </h4>
            <ol className="library-guide-steps">
              {PERSONAL_STEPS.map((step, i) => <li key={i}>{step}</li>)}
            </ol>
          </div>
          <div className="library-guide-col">
            <h4 className="library-guide-col-head">
              <Building2 size={16} strokeWidth={1.75} aria-hidden="true" />
              <span>For your organisation</span>
            </h4>
            <ol className="library-guide-steps">
              {ORG_STEPS.map((step, i) => <li key={i}>{step}</li>)}
            </ol>
          </div>
        </div>
        <div className="library-hint">
          Prefer working locally with Claude Code? Drop the unzipped folder into your local <code>~/.claude/skills/</code> directory instead.
        </div>
      </section>
    </div>
  );
}
